import React, { useState, useEffect, useRef } from 'react';
import StepPersonalInfo from './StepPersonalInfo';
import StepBranch from './StepBranch';
import StepExperience from './StepExperience';
import { submitApplication } from '../api/submitApplication';
import { sendNotifications } from '../api/sendNotifications';
import { uploadCV } from '../api/uploadCv';
import { useTenantConfigQuery } from '../api/queries';
const TOTAL_STEPS = 3;

export default function WizardForm({ branches, loadingBranches, onSuccess }) {
  const { data: tenantConfig } = useTenantConfigQuery();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', father_name: '', email: '', phone: '',
    gender: '', cnic: '', address: '', resume: null,
    branch_id: '', branch_name: '', cid: '', positions: [],
    position_applying: '', job_title: '', experience: '',
    qualification: '', expected_salary: '', how_did_you_hear: '',
  });
  const formRef = useRef(null);

  useEffect(() => {
    if (window.innerWidth < 992) {
      setTimeout(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, []);

  const update = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const validateStep = () => {
    const errs = {};
    if (step === 0) {
      if (!formData.first_name.trim()) errs.first_name = 'Required';
      if (!formData.last_name.trim()) errs.last_name = 'Required';
      if (!formData.father_name.trim()) errs.father_name = 'Required';
      if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) errs.email = 'Valid email required';
      if (!/^\d{11}$/.test(formData.phone)) errs.phone = 'Must be 11 digits';
      if (!formData.gender) errs.gender = 'Required';
      if (formData.cnic && !/^\d{13}$/.test(formData.cnic)) errs.cnic = 'Must be 13 digits';
      if (!formData.address.trim()) errs.address = 'Required';
      if (!formData.resume) errs.resume = 'Please upload your CV / Resume';
    } else if (step === 1) {
      if (!formData.branch_id) errs.branch_id = 'Please select a branch';
    } else if (step === 2) {
      if (!formData.position_applying) errs.position_applying = 'Required';
      if (!formData.experience) errs.experience = 'Required';
      if (!formData.qualification) errs.qualification = 'Required';
      if (!formData.expected_salary.trim()) errs.expected_salary = 'Required';
      if (!formData.how_did_you_hear) errs.how_did_you_hear = 'Required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const next = () => { if (validateStep()) setStep(s => Math.min(s + 1, TOTAL_STEPS - 1)); };
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      let finalData = { ...formData };

      // Upload CV now — branch_name and company_name are available for PHP folder/file naming
      if (formData.resume && !formData.resume_link) {
        const uploadResult = await uploadCV(formData.resume, {
          branch_name:  formData.branch_name  || '',
          company_name: tenantConfig?.site_name || tenantConfig?.enterprise_name || '',
        });
        finalData.resume_link = uploadResult.url;
      }

      const res = await submitApplication(finalData);
      const appData = res?.data || res;
      const applicationId = appData?.application_id || appData?.data?.application_id || '';

      // Send email + WhatsApp in background (don't block success screen)
      sendNotifications(finalData, applicationId);

      onSuccess(appData);
    } catch (err) {
      if (err.message?.includes('UPLOAD')) {
        // Go back to step 0 so user can see the CV error and re-upload
        setStep(0);
        setErrors(prev => ({ ...prev, resume: 'CV upload failed — please select your CV again and retry.' }));
      } else {
        alert('Submission failed. Please try again.');
      }
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const percent = ((step + 1) / TOTAL_STEPS) * 100;

  return (
    <div className="wizard-container" ref={formRef}>
      {submitting && (
        <div className="loader-overlay">
          <div className="loader-circle"></div>
        </div>
      )}

      <div className="progress-bar-wrapper">
        <div className="progress-info">{step + 1} of {TOTAL_STEPS}</div>
        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${percent}%` }}></div>
        </div>
      </div>

      {step === 0 && <StepPersonalInfo data={formData} errors={errors} update={update} />}
      {step === 1 && <StepBranch data={formData} errors={errors} update={update} branches={branches} loading={loadingBranches} />}
      {step === 2 && <StepExperience data={formData} errors={errors} update={update} />}

      <div className="wizard-buttons">
        {step > 0 ? (
          <button className="btn btn-secondary" onClick={prev}>Previous</button>
        ) : (
          <div></div>
        )}
        {step < TOTAL_STEPS - 1 ? (
          <button className="btn btn-primary" onClick={next}>Next</button>
        ) : (
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting && <span className="loader-circle" style={{ width: 16, height: 16, borderWidth: 2, display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }}></span>}
            Submit
          </button>
        )}
      </div>

      <div className="mobile-copy-text">
        ©{new Date().getFullYear()} Powered by{' '}
        <a href="https://fusionlogics.com" target="_blank" rel="noreferrer">Fusion Logics</a>
      </div>
    </div>
  );
}
